import { selectFrom, selectSingleFrom } from '../utils/dom';
import BaseController from './BaseController';

export const ELEMENT_CONTROLLER_ID = 'ElementController';
export const ELEMENT_CONTROLLER_ROOT_ATTRIBUTE = 'data-controller';

/**
 * Controller bound to an element.
 *
 * Requires a data attribute with the controller name on an element that will
 * then be the controller's root. If a root doesn't exist on the page, the
 * controller will not be initialized.
 */
export default class ElementController extends BaseController {
  /**
   * @see BaseController._baseId
   */
  static _baseId = ELEMENT_CONTROLLER_ID;

  /**
   * The controller root DOM node.
   *
   * @member {?Element}
   */
  _root = null;

  /**
   * Flag indicating if the intersection observer has triggered for the
   * controller root element.
   *
   * @member {boolean}
   */
  _didIntersect = false;

  /**
   * Percentage of the controller root element (between 0 and 1) that must be
   * visible for `seen` to trigger.
   *
   * @see IntersectionObserver.thresholds
   * @member {number}
   */
  seenThreshold = 0;

  /**
   * Additional buffer/distance to the controller root element for triggering
   * `seen`. Is a CSS `margin` value.
   *
   * @see IntersectionObserver.rootMargin
   * @member {string}
   */
  seenMargin = '200px';

  /**
   * Initialize base controller.
   *
   * @param {Element} rootElement - The controller's root element.
   */
  _init(rootElement) {
    // Keep non-init related statements in sync with LazyElementController.
    this._root = rootElement;
    this._observeRootIntersection();

    this._initBase();
  }

  /**
   * Check for intersection of the controller root with the viewport if the
   * child class has a `seen` defined.
   */
  _observeRootIntersection() {
    // Trying to create one can cause errors in tests.
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    // Silly but functional - check if the function body for seen contains
    // any code. If it does it must have been overridden in a subclass.
    // Handle it being defined without braces as `seen = () => [code]`.
    // It's sort of wrong since a return value is never used, but it's not
    // invalid.
    const seenFunc = String(this.seen);
    const seenBody = seenFunc
      .slice(seenFunc.indexOf('{') + 1, seenFunc.lastIndexOf('}'))
      .trim();
    if (seenBody !== '' || seenFunc.includes('=>')) {
      let observer = new IntersectionObserver(
        // No need to loop over entries since it's always just the controller
        // instance root being observed.
        ([entry]) => {
          if (entry.isIntersecting) {
            this._didIntersect = true;
            observer.disconnect();
            observer = null;
            this.seen();
          }
        },
        {
          threshold: this.seenThreshold,
          rootMargin: this.seenMargin,
        },
      );
      observer.observe(this.getRoot());
    }
  }

  // IMPORTANT: keep the function body empty for the test in _init.
  /**
   * Called when scrolled near the controller root element. Configure distance
   * with `seenThreshold` and `seenMargin`.
   */
  seen() {}

  /**
   * Called on load, trigger load in base controller.
   */
  _load() {
    this._loadBase();
  }

  /**
   * Get the controller's root element.
   *
   * @deprecated Use this.root instead
   *
   * @returns {Element}
   */
  getRoot() {
    return this._root;
  }

  /**
   * Get the controller's root element.
   *
   * @returns {Element}
   */
  get root() {
    return this._root;
  }

  /**
   * Get nodes contained inside the controller root.
   *
   * @ignore
   * @param {string} selector - Node selector.
   * @param {Function} selectionFunc - Selection function.
   * @param {Element} [base] - Element to select from. Default to controller
   *   root.
   * @returns {Array.<Element> | ?Element} An array of found elements or a
   *   single found element, can be empty or null.
   */
  _getEl(selector, selectionFunc, base = this._root) {
    return selectionFunc(base, selector);
  }

  /**
   * Get nodes contained inside the controller root.
   *
   * @param {string} selector - Node selector. Prefer a name set with data
   *   attribute if possible.
   * @param {Element} [base] - Element to select from. Default to controller
   *   root.
   * @returns {Array.<Element>} An array of found elements, can be empty.
   */
  getNodes(selector, base) {
    return this._getEl(selector, selectFrom, base);
  }

  /**
   * Get a single node contained inside the controller root.
   *
   * @param {string} selector - Node selector. Prefer a name set with data
   *   attribute if possible.
   * @param {Element} [base] - Element to select from. Default to controller
   *   root.
   * @returns {?Element} Found element or null.
   */
  getSingleNode(selector, base) {
    return this._getEl(selector, selectSingleFrom, base);
  }
}
